import { useEffect, useState, useCallback } from 'react'
import { analyticsService } from '@/services/analytics'
import { CoreController } from '@/services/coreController'

export type Platform = 'ios' | 'android' | 'desktop' | 'unknown'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export interface UsePWAInstallReturn {
  platform: Platform
  canInstall: boolean
  isInstalled: boolean
  deferredPrompt: BeforeInstallPromptEvent | null
  showInstall: () => Promise<void>
  showIosGuide: () => void
  showAndroidGuide: () => void
}

const getPlatform = (): Platform => {
  const ua = window.navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'desktop'
}

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [platform, setPlatform] = useState<Platform>('unknown')
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true

    setIsInstalled(isStandalone)
    setPlatform(getPlatform())

    if (isStandalone) return

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
      analyticsService.trackEvent('pwa_installed', { platform: getPlatform() })
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const showInstall = useCallback(async () => {
    const currentPlatform = getPlatform()
    analyticsService.trackEvent('pwa_install_clicked', { platform: currentPlatform })

    if (!deferredPrompt) {
      const appName = CoreController.instance.appSettingResponse.config?.APP_NAME ?? 'EasyService'
      alert(
        `Pour installer ${appName} :\n\n1. Ouvrez le menu du navigateur (⋮)\n2. Sélectionnez "Installer l'application" ou "Ajouter à l'écran d'accueil"\n\nAssurez-vous d'utiliser Chrome/Edge/Safari sur HTTPS.`
      )
      analyticsService.trackEvent('pwa_install_manual_instructions', { platform: currentPlatform })
      return
    }

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      analyticsService.trackEvent('pwa_install_accepted', { platform: currentPlatform })
    } else {
      analyticsService.trackEvent('pwa_install_dismissed', { platform: currentPlatform })
    }

    setDeferredPrompt(null)
  }, [deferredPrompt])

  const showIosGuide = useCallback(() => {
    analyticsService.trackEvent('pwa_ios_guide_opened')
  }, [])

  const showAndroidGuide = useCallback(() => {
    analyticsService.trackEvent('pwa_android_guide_opened')
  }, [])

  const canInstall =
    !isInstalled &&
    (platform === 'ios' || platform === 'android' || !!deferredPrompt || platform === 'desktop')

  return {
    platform,
    canInstall,
    isInstalled,
    deferredPrompt,
    showInstall,
    showIosGuide,
    showAndroidGuide,
  }
}
